import { discoveryApiRef, fetchApiRef, useApi } from "@backstage/core-plugin-api";
import {
	AVD_PLUGIN_ID,
	isSwaBundleInFlight,
	SWA_BUNDLES_MINE_PATH,
	type SwaBundle,
	type SwaBundlesResponse,
} from "@internal/backstage-plugin-avd-common";
import { useCallback, useEffect, useState } from "react";

/** How often the page re-reads while a bundle is being set up. */
export const POLL_INTERVAL_MS = 15_000;

/** The colleague's bundles, or `failed` when the registry could not be read (never a false empty list). */
export type MyWebAppsState =
	| { loading: true }
	| { loading: false; failed: true }
	| { loading: false; failed: false; bundles: SwaBundle[] };

/** The state plus a way to read again after a failure. */
export type MyWebApps = MyWebAppsState & { retry: () => void };

/** Reads the signed-in colleague's web app bundles, re-polling while any is in flight. */
export function useMyWebApps(): MyWebApps {
	const discoveryApi = useApi(discoveryApiRef);
	const fetchApi = useApi(fetchApiRef);
	const [state, setState] = useState<MyWebAppsState>({ loading: true });
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		let active = true;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const load = async () => {
			try {
				const baseUrl = await discoveryApi.getBaseUrl(AVD_PLUGIN_ID);
				const res = await fetchApi.fetch(`${baseUrl}${SWA_BUNDLES_MINE_PATH}`);
				if (!res.ok) throw new Error(`registry responded ${res.status}`);
				const { bundles } = (await res.json()) as SwaBundlesResponse;
				if (!active) return;
				setState({ loading: false, failed: false, bundles });
				if (bundles.some((b) => isSwaBundleInFlight(b.status))) {
					timer = setTimeout(load, POLL_INTERVAL_MS);
				}
			} catch {
				if (active) setState({ loading: false, failed: true });
			}
		};
		void load();
		return () => {
			active = false;
			if (timer) clearTimeout(timer);
		};
	}, [discoveryApi, fetchApi, attempt]);

	const retry = useCallback(() => {
		setState({ loading: true });
		setAttempt((n) => n + 1);
	}, []);

	return { ...state, retry };
}
